//#region IMPORTS
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { clearInstoreBasket } from "./instoreBasketSlice";
import ArrowIconRight from "../../components/icons/ArrowIconRight";
import GoBackButton from "../../components/genericButtons/goBackButton/GoBackButton";
import InstoreBasketPageItem from "./InstoreBasketPageItem";
import BasketEmpty from "../../components/basket/BasketEmpty";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import { formatPrice } from "../../helpers/formatPrice";
//#endregion

const ctaBase =
  "min-w-[10rem] py-4 px-4 mb-4 font-medium border border-black bg-white text-black";

export default function InstoreBasketPage() {
  //#region SETUP
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { items, template_id, store: impersonatedStore } = useAppSelector(
    (state) => state.instoreBasket
  );
  const { user } = useAppSelector((state) => state.account);
  const orderStore = impersonatedStore ?? user?.store ?? null;

  const [deleteWarning, setDeleteWarning] = useState(false);
  //#endregion

  //#region LOGIC
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  // Sum of line totals; only count lines that actually carry a price so an
  // older/price-less basket doesn't render a misleading "€ 0,00".
  const hasAnyPrice = items.some((item) => typeof item.price === "number");
  const totalPrice = items.reduce(
    (sum, item) =>
      typeof item.price === "number" ? sum + item.price * item.quantity : sum,
    0,
  );
  const hasPersoItems = items.some(
    (item) => (item.custom_fields?.length ?? 0) > 0
  );

  const handleDeleteBasket = () => {
    dispatch(clearInstoreBasket());
    setDeleteWarning(false);
    toast.success(t("rituals.instoreBasketEmptied"));
    history.push("/");
  };

  const handleAddMore = () => {
    if (template_id) history.push(`/promos/instore/${template_id}`);
  };

  const handleNextStep = () => {
    history.push("/instore-basket/confirm");
  };
  //#endregion

  //#region FAIL SAFE
  if (items.length === 0) return <BasketEmpty />;
  //#endregion

  //#region RENDER
  return (
    <main>
      <PageHeader
        title={t("rituals.myInstoreBasket")}
        extra={
          <Fragment>
            <div className="text-right">
              <div>
                {orderStore && (
                  <p>
                    <b>
                      {orderStore.external_store_id} — {orderStore.store_name}
                    </b>
                  </p>
                )}
                <p>
                  <b>{t("rituals.summary")}</b>
                </p>
                <p>
                  {t("rituals.totalQuantity")}: {itemCount}
                </p>
                {hasAnyPrice && (
                  <p>
                    <b>
                      {t("rituals.totalPrice")}: {formatPrice(totalPrice)}
                    </b>
                  </p>
                )}
              </div>
            </div>
          </Fragment>
        }
      >
        <GoBackButton />
      </PageHeader>

      <section className="w-full mb-4">
        {items.map((item) => (
          <InstoreBasketPageItem key={item.template_row_id} item={item} />
        ))}
      </section>

      <footer className="w-full md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          {!deleteWarning ? (
            <Fragment>
              <button onClick={handleAddMore} className={ctaBase}>
                {t("rituals.addMore")}
              </button>
              <button
                onClick={() => setDeleteWarning(true)}
                className={`${ctaBase} hover:bg-[#f25c5c] hover:text-black`}
              >
                {t("rituals.deleteBasket")}
              </button>
              {hasPersoItems && (
                <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800">
                  {t("rituals.basketPersoRequired")}
                </span>
              )}
            </Fragment>
          ) : (
            <div className="flex justify-center items-center mb-4 bg-white">
              <h3 className="text-2xl mr-4">{t("rituals.areYouSure")}</h3>
              <div>
                <button
                  onClick={handleDeleteBasket}
                  className="min-w-[10rem] py-4 px-4 border border-black bg-black text-white font-bold"
                >
                  {t("rituals.yes")}
                </button>
                <button
                  onClick={() => setDeleteWarning(false)}
                  className="min-w-[10rem] py-4 px-4 border border-black bg-white text-[#f25c5c] font-bold"
                >
                  {t("rituals.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleNextStep}
          className="w-full md:w-auto flex items-center justify-center gap-4 min-w-[10rem] py-4 px-4 mb-4 font-semibold border border-black bg-[#3e5902] text-white lg:text-lg"
        >
          <span>{t("rituals.nextStep")}</span>
          <ArrowIconRight color="white" />
        </button>
      </footer>
    </main>
  );
  //#endregion
}
